import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { waLink } from "@/data/site";

const LINKS = [
  { href: "#schmuck", label: "Schmuck", id: "nav-link-schmuck" },
  { href: "#suits", label: "Suits", id: "nav-link-suits" },
  { href: "#dupatta", label: "Dupatta", id: "nav-link-dupatta" },
  { href: "#ueber-uns", label: "Über uns", id: "nav-link-ueber-uns" },
  { href: "#kontakt", label: "Kontakt", id: "nav-link-kontakt" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-wine/70 backdrop-blur-xl border-b border-gold/10">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 transition-opacity duration-300 hover:opacity-80"
        >
          <LogoMark className="h-8 w-8" />
          <span className="leading-none">
            <span className="block font-serif text-2xl tracking-tight text-cream">
              APSARA<span className="text-gold">.</span>
            </span>
            <span className="mt-1 block text-[0.55rem] font-light uppercase tracking-[0.4em] text-gold/90">
              Kollektion
            </span>
          </span>
        </a>

        <div className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="text-sm font-light uppercase tracking-widest text-rosy transition-colors duration-300 hover:text-gold"
            >
              {l.label}
            </a>
          ))}
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp"
            className="flex items-center gap-2 rounded-full border border-gold px-5 py-2 text-sm uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-wine"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
        </div>

        <button
          className="text-gold md:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid={open ? "nav-menu-close" : "nav-menu-open"}
          aria-label="Menü öffnen"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="bg-wine/95 backdrop-blur-xl md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6 px-6 py-10">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`${l.id}-mobile`}
                  onClick={() => setOpen(false)}
                  className="font-serif text-4xl font-light text-cream transition-colors duration-300 hover:text-gold"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-cta-whatsapp-mobile"
                onClick={() => setOpen(false)}
                className="mt-2 flex w-fit items-center gap-2 rounded-full bg-wa px-6 py-3 text-sm font-medium uppercase tracking-widest text-white"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp schreiben
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
